// api/axiosConfig.js
import axios from 'axios';
import ApiConfig from './config';   // ✅ Direct import for REFRESH_TOKEN

// Session manager reference (set from AuthContext)
let sessionManagerRef = null;
let onTokenRefreshedRef = null;

export const setSessionManager = (sessionManager) => {
  sessionManagerRef = sessionManager;
};

export const setOnTokenRefreshed = (callback) => {
  onTokenRefreshedRef = callback;
};

// Create axios instance
const axiosInstance = axios.create({
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// ---- Request interceptor: attach current token ----
axiosInstance.interceptors.request.use(
  (config) => {
    if (sessionManagerRef && sessionManagerRef.isAuthenticated()) {
      const session = sessionManagerRef.getSession();
      if (session && session.token) {
        config.headers.Authorization = `Bearer ${session.token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---- Response interceptor: 401 → refresh → retry ----
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip refresh/logout endpoints to avoid loops
    if (
      originalRequest.url?.includes('RefreshToken') ||
      originalRequest.url?.includes('LogoutUser')
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (!sessionManagerRef) {
        return Promise.reject(error);
      }

      // Queue concurrent requests while refresh is in flight
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const currentSession = sessionManagerRef.getSession();
        const refreshToken = currentSession?.refreshToken;

        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        console.log('[axios] 401 received — refreshing token...');
        console.log('[axios] Calling:', ApiConfig.ENDPOINTS.REFRESH_TOKEN);

        // Use raw axios for refresh (avoids interceptor recursion)
        const response = await axios({
          method: 'PUT',
          url: ApiConfig.ENDPOINTS.REFRESH_TOKEN,
          data: { refreshToken },
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.data.token) {
          throw new Error('No token in refresh response');
        }

        const newExpirySecs =
          response.data.expirySecs || currentSession.expirySecs || 900;

        sessionManagerRef.setSession(
          response.data.token,
          response.data.refreshToken || refreshToken,
          currentSession.userName,
          newExpirySecs
        );

        // Notify SessionContext so React state stays in sync
        if (onTokenRefreshedRef) {
          try {
            onTokenRefreshedRef(response.data.token, newExpirySecs);
          } catch (cbErr) {
            console.warn('[axios] onTokenRefreshed callback error:', cbErr);
          }
        }

        console.log('[axios] Token refreshed successfully.');

        processQueue(null, response.data.token);

        originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('[axios] Refresh failed:', refreshError);

        processQueue(refreshError, null);

        // Only hard-logout if the refresh token itself was rejected
        const status = refreshError?.response?.status;
        if (status === 401 || status === 403) {
          if (sessionManagerRef) {
            sessionManagerRef.clearSession();
          }
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;